import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useDatabase } from '../context/DatabaseContext';
import { Eye, Edit, Trash2, Search, Plus, X, FileText, Download, Image } from 'lucide-react';
import toast from 'react-hot-toast';

const ViewCustomers = () => {
  const { state, dispatch } = useApp();
  const { customers, loading, deleteCustomer, loadCustomers } = useDatabase();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  // Load customers on component mount
  useEffect(() => {
    loadCustomers();
  }, []);

  const filteredCustomers = customers.filter(customer =>
    (customer.full_name || customer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.mobile_number || customer.mobile || '').includes(searchTerm) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(id);
        // Success message is handled by the database context
      } catch (error) {
        // Error message is handled by the database context
      }
    }
  };

  const handleEdit = (customer) => {
    // Navigate to edit customer page with customer data
    navigate('/add-customer', { state: { editMode: true, customer } });
  };

  const downloadDocument = (doc, customerName) => {
    try {
      // Create a link element
      const link = document.createElement('a');
      link.href = doc.data;
      link.download = `${customerName}_${doc.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Document downloaded successfully');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const CustomerModal = ({ customer, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Customer Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            {customer.photo && (
              <img
                src={customer.photo}
                alt={customer.name}
                className="w-32 h-32 object-cover rounded-lg mb-4"
              />
            )}
            <div className="space-y-3">
              <div>
                <label className="font-medium text-gray-700">Name:</label>
                <p className="text-gray-900">{customer.name}</p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Father's Name:</label>
                <p className="text-gray-900">{customer.fatherName || 'N/A'}</p>
              </div>
              <div>
                <label className="font-medium text-gray-700">Mother's Name:</label>
                <p className="text-gray-900">{customer.motherName || 'N/A'}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="font-medium text-gray-700">Mobile:</label>
              <p className="text-gray-900">{customer.mobile}</p>
            </div>
            <div>
              <label className="font-medium text-gray-700">Email:</label>
              <p className="text-gray-900">{customer.email || 'N/A'}</p>
            </div>
            <div>
              <label className="font-medium text-gray-700">Address:</label>
              <p className="text-gray-900">{customer.address || 'N/A'}</p>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <h3 className="font-medium text-gray-700 mb-3">Supporting Documents:</h3>
          {customer.documents && customer.documents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customer.documents.map((doc, index) => {
                const isImage = doc.data && doc.data.startsWith('data:image/');
                return (
                  <div key={doc.id || index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        {isImage ? (
                          <Image className="w-5 h-5 text-blue-600" />
                        ) : (
                          <FileText className="w-5 h-5 text-gray-600" />
                        )}
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {doc.name || `Document ${index + 1}`}
                        </span>
                      </div>
                      <div className="flex space-x-2">
                        {doc.data && (
                          <>
                            <button
                              onClick={() => window.open(doc.data, '_blank')}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                              title="View Document"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => downloadDocument(doc, customer.name)}
                              className="text-green-600 hover:text-green-800 p-1 rounded transition-colors"
                              title="Download Document"
                            >
                              <Download size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {isImage && doc.data && (
                      <div className="mt-2">
                        <img
                          src={doc.data}
                          alt={doc.name || `Document ${index + 1}`}
                          className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(doc.data, '_blank')}
                        />
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      <p>Added: {new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No documents uploaded</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold gradient-text">Customers</h1>
          <p className="text-gray-600 text-lg mt-2">Manage your customer database</p>
        </div>
        <Link to="/add-customer" className="btn-primary">
          <Plus className="w-5 h-5 mr-2" />
          Add Customer
        </Link>
      </div>

      <div className="card">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>

        {filteredCustomers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Added On
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {customer.photo ? (
                          <img
                            src={customer.photo}
                            alt={customer.name}
                            className="w-10 h-10 object-cover rounded-full mr-3"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center mr-3">
                            <span className="text-white font-medium">
                              {customer.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {customer.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {customer.fatherName && `S/O ${customer.fatherName}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.mobile}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.email || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setSelectedCustomer(customer)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(customer)}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Customer"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Customer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No customers found</p>
            <Link to="/add-customer" className="btn-primary mt-4 inline-block">
              Add Your First Customer
            </Link>
          </div>
        )}
      </div>

      {selectedCustomer && (
        <CustomerModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </div>
  );
};

export default ViewCustomers;
